import { TrashBin } from '@gravity-ui/icons'
import { Button, Table, Tooltip } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { TimesheetDateRangePicker } from '@/components/ui/TimesheetDateRangePicker'
import { useTimesheetStore } from '@/store/timesheet.store'
import type { TimeReportItem } from '@/types/timeReport'

import { TimesheetActivityAutocomplete } from './TimesheetActivityAutocomplete'

interface TimesheetActivityTableRowProps {
  index: number;
  item: TimeReportItem;
  onChange: (index: number, updatedFields: Partial<TimeReportItem>) => void;
  onDelete: () => void;
  isReadOnly?: boolean;
  showErrors?: boolean;
}

export function TimesheetActivityTableRow({
  index,
  item,
  onChange,
  onDelete,
  isReadOnly = false,
  showErrors = false,
}: TimesheetActivityTableRowProps) {
  const { t, i18n } = useTranslation()
  const { filters } = useTimesheetStore()

  const year = filters.year
  const month = filters.month
  const lastDay = new Date(year, month, 0).getDate()
  const minDateStr = `${year}-${String(month).padStart(2, '0')}-01`
  const maxDateStr = `${year}-${String(month).padStart(2, '0')}-${lastDay}`

  const formatDate = (dateStr: string) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return date.toLocaleDateString(i18n.resolvedLanguage === 'ru' ? 'ru-RU' : 'en-US', {
      month: 'short',
      day: 'numeric',
    })
  }

  const dateValue = item.StartDate && item.EndDate
    ? { start: item.StartDate.split('T')[0], end: item.EndDate.split('T')[0] }
    : null

  const handleDateChange = (val: { start: string; end: string } | null) => {
    if (val) {
      onChange(index, {
        StartDate: val.start,
        EndDate: val.end,
      })
    } else {
      onChange(index, {
        StartDate: '',
        EndDate: '',
      })
    }
  }

  if (isReadOnly) {
    return (
      <Table.Row className="hover:bg-zinc-50/50 dark:hover:bg-zinc-800/30 border-b border-divider/50">
        <Table.Cell className="px-2 py-3 min-w-[280px]">
          <div className="flex items-center gap-2 text-xs text-foreground/70">
            <span className="bg-default-100 dark:bg-zinc-800 px-2.5 py-1.5 rounded-lg font-medium">
              {formatDate(item.StartDate)}
            </span>
            <span className="text-foreground/40">—</span>
            <span className="bg-default-100 dark:bg-zinc-800 px-2.5 py-1.5 rounded-lg font-medium">
              {formatDate(item.EndDate)}
            </span>
          </div>
        </Table.Cell>
        <Table.Cell className="px-2 py-3 w-full text-sm font-medium text-foreground">
          {item.Activity}
        </Table.Cell>
      </Table.Row>
    )
  }

  return (
    <Table.Row className="hover:bg-zinc-50/50 dark:hover:bg-zinc-800/30 border-b border-divider/50">
      <Table.Cell className="px-2 py-1.5 min-w-[280px]">
        <TimesheetDateRangePicker
          value={dateValue}
          onChange={handleDateChange}
          variant="secondary"
          minValue={minDateStr}
          maxValue={maxDateStr}
          placeholderValue={minDateStr}
          disableNavigation
          isInvalid={showErrors && (!item.StartDate || !item.EndDate)}
        />
      </Table.Cell>
      <Table.Cell className="px-2 py-1.5 w-full">
        <TimesheetActivityAutocomplete
          value={item.Activity || ''}
          onChange={val => onChange(index, { Activity: val })}
          placeholder={t('timesheet.activity_placeholder', 'Выберите активность')}
          variant="secondary"
          isInvalid={showErrors && (!item.Activity?.trim())}
        />
      </Table.Cell>
      <Table.Cell className="px-2 py-1.5 text-center">
        <Tooltip delay={0}>
          <Button
            isIconOnly
            size="sm"
            variant="danger-soft"
            onClick={onDelete}
          >
            <TrashBin className="size-4" />
          </Button>
          <Tooltip.Content>
            <p>{t('common.delete')}</p>
          </Tooltip.Content>
        </Tooltip>
      </Table.Cell>
    </Table.Row>
  )
}
